import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Spinner, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

const FileUpload = ({
  formData,
  setFormData,
  inputKey,
  mrn,
  imageUrl,
  setImageUrl,
  mode = "single", // "single" or "multiple"
  branch = "",
  treatmentType = "", // Optional: e.g. "PRP", "Botox"
}) => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploadStatus, setUploadStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Camera States
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Generate object URLs for file previews (Used in multiple mode)
  useEffect(() => {
    if (mode === "single" || !files.length) {
      setPreviews([]);
      return;
    }

    const objectUrls = files.map((file) => {
      if (file.type.startsWith("image/")) {
        return { type: "image", url: URL.createObjectURL(file), name: file.name };
      }
      return { type: "pdf", url: null, name: file.name };
    });

    setPreviews(objectUrls);

    return () => {
      objectUrls.forEach((item) => {
        if (item.url) URL.revokeObjectURL(item.url);
      });
    };
  }, [files, mode]);

  // Core Upload Logic
  const executeUpload = async (filesToUpload) => {
    if (!filesToUpload || filesToUpload.length === 0) return;

    setIsLoading(true);
    setUploadStatus("");

    try {
      const uploadedKeys = [];

      for (const file of filesToUpload) {
        const response = await axios.get(
          `${process.env.REACT_APP_CRI_API}get-upload-url`,
          {
            params: { fileName: file.name, mrn, branch, treatmentType, folder:"profile-photos" },
          }
        );

        const { uploadUrl, fileKey } = response.data;

        await axios.put(uploadUrl, file, {
          headers: { "Content-Type": file.type },
        });

        uploadedKeys.push(fileKey);
      }

      // Update Parent State Based on Mode
      setFormData((prev) => {
        const currentData = { ...prev };

        if (mode === "single") {
          currentData.photoKey = uploadedKeys[0];
          if (treatmentType) {
            currentData.treatmentPhotos = {
              ...(currentData.treatmentPhotos || {}),
              [treatmentType]: uploadedKeys[0],
            };
          }
        } else {
          const existingKeys = currentData.fileKeys || [];
          currentData.fileKeys = [...existingKeys, ...uploadedKeys];

          if (treatmentType) {
            const existingTreatmentKeys =
              currentData.treatmentPhotos?.[treatmentType] || [];
            currentData.treatmentPhotos = {
              ...(currentData.treatmentPhotos || {}),
              [treatmentType]: Array.isArray(existingTreatmentKeys)
                ? [...existingTreatmentKeys, ...uploadedKeys]
                : [existingTreatmentKeys, ...uploadedKeys],
            };
          }
        }

        return currentData;
      });

      if (setImageUrl && uploadedKeys.length > 0) {
        // Direct local preview or uploaded S3 key
        if (mode === "single" && filesToUpload[0].type.startsWith("image/")) {
          setImageUrl(URL.createObjectURL(filesToUpload[0]));
        } else {
          setImageUrl(uploadedKeys[0]);
        }
      }

      setFiles([]);
    } catch (error) {
      console.error("Upload error:", error);
      setUploadStatus("Upload failed! Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const processSelectedFiles = (selectedFiles) => {
    if (!selectedFiles.length) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
    ];

    const validFiles = selectedFiles.filter((file) => {
      if (!allowedTypes.includes(file.type)) {
        setUploadStatus(`Invalid file type: ${file.name}`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        setUploadStatus(`File too large: ${file.name} (Max 2MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    if (mode === "single") {
      // Auto-upload immediately on change in Single Mode
      executeUpload([validFiles[0]]);
    } else {
      // Queue up in Multiple Mode
      setFiles((prev) => [...prev, ...validFiles]);
      setUploadStatus("");
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    processSelectedFiles(selectedFiles);
  };

  const handleRemovePendingFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  // --- Camera Operations ---
  const startCamera = async () => {
    setIsCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera access error:", err);
      setUploadStatus("Unable to access camera.");
      setIsCameraOpen(false);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setIsCameraOpen(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const capturedFile = new File([blob], `photo_${Date.now()}.jpg`, {
          type: "image/jpeg",
        });

        processSelectedFiles([capturedFile]);
        stopCamera();
      },
      "image/jpeg",
      0.9
    );
  };

  return (
    <div className="p-2 border rounded bg-white shadow-sm">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-bold m-0">
          Photo & Document Upload ({mode.toUpperCase()} MODE)
        </h6>
        {treatmentType && (
          <span className="badge bg-info text-dark">
            Treatment: {treatmentType}
          </span>
        )}
      </div>

      {/* Control Actions */}
      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
        <input
          type="file"
          accept="application/pdf, image/*"
          multiple={mode === "multiple"}
          onChange={handleFileChange}
          key={inputKey}
          id="file-input-element"
          style={{ display: "none" }}
          disabled={isLoading}
        />

        <label
          htmlFor="file-input-element"
          className={`btn btn-outline-primary btn-sm m-0 ${isLoading ? "disabled" : ""}`}
        >
          📁 {mode === "multiple" ? "Select File(s)" : "Upload Photo"}
        </label>

        <Button color="outline-success" size="sm" onClick={startCamera} disabled={isLoading}>
          📷 Capture Photo
        </Button>

        {/* Manual upload button shown only in Multiple mode */}
        {mode === "multiple" && mrn && files.length > 0 && (
          <Button
            color="primary"
            size="sm"
            onClick={() => executeUpload(files)}
            disabled={isLoading}
          >
            {isLoading ? <Spinner size="sm">{""}</Spinner> : `Upload (${files.length})`}
          </Button>
        )}

        {isLoading && mode === "single" && (
          <div className="d-flex align-items-center gap-2 text-primary small">
            <Spinner size="sm" /> Uploading...
          </div>
        )}
      </div>

      {uploadStatus && <p className="text-danger small m-0 mb-2">{uploadStatus}</p>}

      {/* Active Photo Preview */}
      {imageUrl && (
        <div className="mb-3">
          <span className="small text-muted d-block mb-1">Active Image:</span>
          <img
            src={imageUrl}
            alt="Current Active Preview"
            className="rounded border shadow-sm"
            style={{ width: "110px", height: "110px", objectFit: "cover" }}
          />
        </div>
      )}

      {/* Pending Queue - Rendered ONLY in Multiple Mode */}
      {mode === "multiple" && previews.length > 0 && (
        <div className="mt-2">
          <span className="small text-muted d-block mb-2">Pending Upload Queue:</span>
          <div className="d-flex flex-wrap gap-2">
            {previews.map((item, index) => (
              <div
                key={index}
                className="position-relative border rounded p-1 bg-light text-center"
                style={{ width: "100px" }}
              >
                <button
                  type="button"
                  className="btn-close position-absolute top-0 end-0 bg-white rounded-circle p-1 m-1 shadow-sm"
                  style={{ width: "6px", height: "6px" }}
                  onClick={() => handleRemovePendingFile(index)}
                  aria-label="Remove"
                ></button>

                {item.type === "image" ? (
                  <img
                    src={item.url}
                    alt={item.name}
                    className="rounded mb-1"
                    style={{ width: "100%", height: "70px", objectFit: "cover" }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center bg-white rounded border mb-1"
                    style={{ height: "70px" }}
                  >
                    <span className="fw-bold text-danger small">PDF</span>
                  </div>
                )}
                <p
                  className="small text-truncate m-0 px-1"
                  style={{ fontSize: "10px" }}
                  title={item.name}
                >
                  {item.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Camera Capture Modal */}
      <Modal isOpen={isCameraOpen} toggle={stopCamera} centered size="md">
        <ModalHeader toggle={stopCamera}>Capture Image</ModalHeader>
        <ModalBody className="text-center p-0 bg-dark">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: "100%", maxHeight: "380px", objectFit: "contain" }}
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </ModalBody>
        <ModalFooter className="justify-content-between">
          <Button color="secondary" size="sm" onClick={stopCamera}>
            Cancel
          </Button>
          <Button color="danger" size="sm" onClick={capturePhoto}>
            📸 Take Snapshot
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default FileUpload;